/**
 * Czy 401 z backendu oznacza naprawdę nieważną sesję Firebase Auth.
 *
 * 401 jednego endpointu nie wystarcza do wylogowania: backend potrafi odrzucić poprawny token
 * z własnego powodu (np. `v3-panelHelpChat` bez zainicjalizowanego Admin SDK zwracał
 * „Invalid token” na każde pytanie do asystenta). Rozstrzyga wymuszone odświeżenie tokenu:
 * udane = sesja żyje, odrzucone przez Firebase z kodem konta/tokenu = sesja nieważna,
 * każdy inny błąd (sieć, limit) = nie wiadomo, więc też nie wylogowujemy.
 */

export type SessionCheck =
  | { status: 'valid'; token: string }
  | { status: 'invalid' }
  | { status: 'unknown' };

interface TokenSource {
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
}

const INVALID_SESSION_CODES = new Set([
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-user-token',
]);

export const checkFirebaseSession = async (user: TokenSource | null | undefined): Promise<SessionCheck> => {
  if (!user) return { status: 'invalid' };
  try {
    const token = await user.getIdToken(true);
    return token ? { status: 'valid', token } : { status: 'invalid' };
  } catch (error) {
    const code = (error as { code?: unknown } | null)?.code;
    return typeof code === 'string' && INVALID_SESSION_CODES.has(code) ? { status: 'invalid' } : { status: 'unknown' };
  }
};

/** Minimum konfiguracji żądania potrzebne do ponowienia (zgodne z InternalAxiosRequestConfig). */
export interface RetriableRequest {
  headers: { Authorization?: unknown };
  sessionRetried?: boolean;
}

export interface UnauthorizedHandlerDeps<C extends RetriableRequest> {
  getUser: () => TokenSource | null | undefined;
  /** Czy nagłówek Authorization dołożył interceptor żądania (token currentUser), a nie wołający. */
  isAutoAuth: (config: C) => boolean;
  /** Ponawia żądanie jako NOWĄ konfigurację z `sessionRetried: true` i podanym tokenem. */
  retry: (config: C, token: string) => Promise<unknown>;
  isOnLoginPage: () => boolean;
  /** Toast + signOut + przejście na /login; zwraca promise zakończenia całej sekwencji. */
  onSessionInvalid: () => Promise<void>;
}

/**
 * Obsługa 401 z backendu. Rozwiązuje się wynikiem ponowionego żądania albo odrzuca pierwotnym
 * błędem. Wylogowanie wyłącznie, gdy `checkFirebaseSession` mówi `invalid`, i najwyżej raz,
 * nawet gdy kilka żądań dostanie 401 naraz.
 */
export const createUnauthorizedHandler = <C extends RetriableRequest>(deps: UnauthorizedHandlerDeps<C>) => {
  let isHandlingInvalid = false;
  // Kilka równoległych 401 sprawdza sesję jednym odświeżeniem tokenu.
  let pendingCheck: Promise<SessionCheck> | null = null;
  const checkOnce = (): Promise<SessionCheck> => {
    pendingCheck ??= checkFirebaseSession(deps.getUser()).finally(() => {
      pendingCheck = null;
    });
    return pendingCheck;
  };

  return async (error: unknown, config: C | undefined): Promise<unknown> => {
    // Wylogowanie już trwa albo użytkownik jest na logowaniu (np. publiczne endpointy auth):
    // nic nie sprawdzamy, wołający dostaje błąd.
    if (isHandlingInvalid || deps.isOnLoginPage()) throw error;
    // Drugie 401 po ponowieniu ze świeżym tokenem to błąd endpointu, nie sesji.
    if (config?.sessionRetried) throw error;

    const session = await checkOnce();
    // W trakcie oczekiwania inne 401 mogło już rozpocząć wylogowanie.
    if (isHandlingInvalid) throw error;

    if (session.status === 'valid') {
      // Nagłówek ustawiony ręcznie przez wołającego: nie podmieniamy go tokenem currentUser.
      if (!config || !deps.isAutoAuth(config)) throw error;
      return deps.retry(config, session.token);
    }
    if (session.status === 'unknown') throw error;

    isHandlingInvalid = true;
    deps.onSessionInvalid().catch(() => {}).finally(() => {
      isHandlingInvalid = false;
    });
    throw error;
  };
};
